import type { ReactNode } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";

type FormCardSectionProps = {
    title: string;
    description?: string;
    /**
     * Vertical gap between body children. Maps to Figma per-section gap:
     *  - 12 px for "Reference packaging" (1/4)
     *  - 24 px for "Identity" (2/4)
     *  - 16 px for "Main element" (3/4) and "Associated elements" (4/4)
     */
    bodyGap?: number;
    children: ReactNode;
};

export const FormCardSection = ({ title, description, bodyGap = 24, children }: FormCardSectionProps) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                overflow: "hidden",
                border: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: "12px",
                backgroundColor: theme.custom.grey[100],
                boxShadow: theme.custom.shadows.grey,
            }}
        >
            <Stack
                spacing={0.5}
                sx={{
                    p: 3,
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    backgroundColor: "#FEFCFB",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "18px",
                        fontWeight: 600,
                        lineHeight: "160%",
                        color: theme.custom.grey[2800],
                    }}
                >
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body2" sx={{ color: theme.custom.grey[1400] }}>
                        {description}
                    </Typography>
                )}
            </Stack>
            <Stack sx={{ p: 3, gap: `${bodyGap}px` }}>{children}</Stack>
        </Box>
    );
};
